import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';

export interface Club {
  id: string;
  name: string;
  description: string;
  category: string;
  city: string;
  coverPhotoUrl?: string;
  feedEnabled: boolean;
  chatEnabled: boolean;
  about?: string;
  rules?: string;
  code?: string; // 8-digit club code, only visible to admin
  memberCount: number;
  meetupCount?: number;
  referralCount?: number;
  membershipStatus?: 'not-member' | 'pending' | 'approved' | 'joined';
  isAdmin?: boolean;
  isCaptain?: boolean; // True if user is a captain of this club
  // Paid membership fields
  isPaid?: boolean;        // true = paid club, false/undefined = free
  annualFee?: number;      // Annual fee in INR (only for paid clubs)
  membershipPaymentStatus?: 'pending' | 'paid' | 'expired'; // current user's payment status
  createdAt: string;
  updatedAt: string;
  members?: ClubMember[];
  joinRequests?: ClubJoinRequest[];
}

export interface ClubMember {
  id: string;
  userId: string;
  role?: 'member' | 'captain'; // Captain role added
  joinedAt: string;
  user: {
    id: string;
    name: string;
    profession?: string;
    photo?: string;
  };
}

export interface ClubJoinRequest {
  id: string;
  userId: string;
  phone: string;
  profession: string;
  reason: string;
  status: 'pending' | 'approved' | 'accepted' | 'rejected';
  createdAt: string;
  user: {
    id: string;
    name: string;
    profession?: string;
    photo?: string;
  };
}

export interface ClubPost {
  id: string;
  clubId: string;
  userId: string;
  text?: string;
  type: 'text' | 'image' | 'poll' | 'link' | 'video';
  imageUrl?: string;
  videoUrl?: string;
  linkUrl?: string;
  likesCount: number;
  commentsCount: number;
  createdAt: string;
  user: {
    id: string;
    name: string;
    profession?: string;
    photo?: string;
  };
}

export interface ClubMeetup {
  id: string;
  clubId: string;
  creatorId: string;
  recipientId: string;
  message: string;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: string;
  creator: {
    id: string;
    name: string;
    profession?: string;
    photo?: string;
  };
  recipient: {
    id: string;
    name: string;
    profession?: string;
    photo?: string;
  };
}

// Get all clubs
export async function getClubs(params?: {
  category?: string;
  city?: string;
  search?: string;
}): Promise<Club[]> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const queryParams = new URLSearchParams();
  if (params?.category) queryParams.append('category', params.category);
  if (params?.city) queryParams.append('city', params.city);
  if (params?.search) queryParams.append('search', params.search);

  const url = buildApiUrl(`/api/clubs${queryParams.toString() ? `?${queryParams}` : ''}`);
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch clubs');
  }

  return response.json();
}

// Get club by ID
export async function getClubById(clubId: string): Promise<Club> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}`);
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch club');
  }

  return response.json();
}

// Create club (admin only)
export async function createClub(data: {
  name: string;
  description: string;
  category: string;
  city: string;
  coverPhotoUrl?: string;
  about?: string;
  rules?: string;
  isPaid?: boolean;
  annualFee?: number;
}): Promise<Club> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl('/api/clubs');
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create club');
  }

  return response.json();
}

// Update club
export async function updateClub(clubId: string, data: {
  name?: string;
  description?: string;
  category?: string;
  city?: string;
  coverPhotoUrl?: string;
  about?: string;
  rules?: string;
  isPaid?: boolean;
  annualFee?: number;
}): Promise<Club> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}`);
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update club');
  }

  return response.json();
}

// Update club code (Admin only)
export async function updateClubCode(clubId: string, code: string): Promise<Club> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/code`);
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update club code');
  }

  return response.json();
}

// Join club
export async function joinClub(clubId: string, data: {
  phone: string;
  profession?: string;
  aboutBusiness?: string;
  industry?: string;
  referredBy?: string;
  socialNetworkId?: string;
  contactSize?: string;
  contactSphere?: string;
  salesTurnover?: string;
  businessPlace?: string;
  cityResidentFrom?: string;
  whyJoining?: string;
  link?: string;
  requestMessage?: string;
  idProof?: string;
}): Promise<ClubJoinRequest> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/join`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to join club');
  }

  return response.json();
}

// Accept join request (admin only)
export async function acceptJoinRequest(clubId: string, requestId: string): Promise<void> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/requests/${requestId}/accept`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept join request');
  }
}

// Reject join request (admin only)
export async function rejectJoinRequest(clubId: string, requestId: string): Promise<void> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/requests/${requestId}/reject`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject join request');
  }
}

// Verify club code and complete joining (user - after admin approval)
export async function verifyCodeAndJoin(clubId: string, code: string): Promise<void> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/verify-code`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify code');
  }
}

// Leave club (member)
export async function leaveClub(clubId: string): Promise<void> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/leave`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to leave club');
  }
}

// Remove member (admin only)
export async function removeMember(clubId: string, userId: string): Promise<void> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/members/${userId}`);
  
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove member');
  }
}

// Make captain (admin only)
export async function makeCaptain(clubId: string, userId: string): Promise<void> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/members/${userId}/make-captain`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to make captain');
  }
}

// Remove captain (admin only)
export async function removeCaptain(clubId: string, userId: string): Promise<void> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/members/${userId}/remove-captain`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove captain');
  }
}

// Update club settings (admin only, or captain for posting days)
export async function updateClubSettings(clubId: string, data: {
  feedEnabled?: boolean;
  chatEnabled?: boolean;
  description?: string;
  rules?: string;
  allowedPostingDays?: number[];
}): Promise<Club> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/settings`);
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update club settings');
  }

  return response.json();
}

// Get club posts
export async function getClubPosts(clubId: string): Promise<ClubPost[]> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/posts`);
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch club posts');
  }

  return response.json();
}

// Create club post
export async function createClubPost(clubId: string, data: {
  text?: string;
  type: 'text' | 'image' | 'poll' | 'link' | 'video';
  imageUrl?: string;
  videoUrl?: string;
  linkUrl?: string;
  mentionedUserIds?: string[];
}): Promise<ClubPost> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/posts`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create club post');
  }

  return response.json();
}

// Get club meetups
export async function getClubMeetups(clubId: string): Promise<ClubMeetup[]> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/meetups`);
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch club meetups');
  }

  return response.json();
}

// Create club meetup
export async function createClubMeetup(clubId: string, data: {
  recipientId: string;
  message: string;
}): Promise<ClubMeetup> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/meetups`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create club meetup');
  }

  return response.json();
}

// Update meetup status
export async function updateMeetupStatus(clubId: string, meetupId: string, status: 'accepted' | 'declined'): Promise<ClubMeetup> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/meetups/${meetupId}/status`);
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update meetup status');
  }

  return response.json();
}

// Club Exit Request APIs

export interface ClubExitRequest {
  id: string;
  clubId: string;
  userId: string;
  note?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  user: {
    id: string;
    name: string;
    profession?: string;
    photo?: string;
    phone?: string;
    email?: string;
  };
  club: {
    id: string;
    name: string;
  };
}

// Submit exit request
export async function submitExitRequest(clubId: string, note?: string): Promise<ClubExitRequest> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/exit-request`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ note }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to submit exit request');
  }

  return response.json();
}

// Get pending exit requests (Admin only)
export async function getPendingExitRequests(clubId: string): Promise<ClubExitRequest[]> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/exit-requests`);
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch exit requests');
  }

  return response.json();
}

// Process exit request (Admin only)
export async function processExitRequest(clubId: string, requestId: string, action: 'approve' | 'reject'): Promise<{ message: string }> {
  const token = await storage.getToken();
  if (!token) throw new Error('Not authenticated');

  const url = buildApiUrl(`/api/clubs/${clubId}/exit-requests/${requestId}`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to process exit request');
  }

  return response.json();
}

/**
 * Delete a club (admin only)
 */
export async function deleteClub(clubId: string): Promise<{ message: string }> {
  const token = await storage.getToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(buildApiUrl(`api/clubs/${clubId}`), {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete club');
  }

  return response.json();
}

